import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { omit } from "lodash";
import PropTypes from "prop-types";
import { useFormik } from "formik";

import Stepper from "common/Stepper";
import BreadCrumb from "Components/Common/BreadCrumb";
import AuthorizedPersonDetails from "Components/AuthorizedPersonDetails/AuthorizedPersonDetails";
import AddressForPower from "Components/AddressForPower";
import DocumentToSubmit from "Components/DocumentsToSubmit";
import Declaration from "Components/Declaration";
import ApplicantDetails from "pages/Consumer/ApplicationType/NetMeteringConnection/ApplicantDetails";
import SolarDetails from "pages/Consumer/ApplicationType/NetMeteringConnection/SolarDetails";
import { applicationTypes } from "common/constant";
import Backdrop from "Components/Common/Backdrop";

import { setValues } from "slices/netMeteringConnection/reducer";
import { useNetMeteringValidationSchema as useValidationSchema } from "hooks";

import { getIsDocumentRequired } from "utils/getIsDocumentRequired";
import {
  generateFormData,
  filterEmptyStrings,
  convertDocumentKeys,
} from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useLazyConsumerByIdQuery } from "apis/User";
import {
  usePostApplicationDetailsMutation,
  useLazyApplicationDetailQuery,
} from "apis/ApplicationRequestDetail";
import { myRequestRoute } from "Routes/constants";

const NetMetering = ({ applicationId, handleClose, t }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [applicationData, setApplicationData] = useState({});

  const user = useSelector((state) => state.Login.user);

  const navigate = useNavigate();

  const [
    getApplicationDetails,
    {
      isLoading: isApplicationDataLoading,
      isFetching: isApplicationDataFetching,
    },
  ] = useLazyApplicationDetailQuery();

  const [
    getUtilityConsumerDetails,
    {
      data: { data: consumerData } = {},
      isLoading: isConsumerDetailsLoading,
      isFetching: isConsumerDataFetching,
    },
  ] = useLazyConsumerByIdQuery();

  const [submitApplication, { isLoading }] =
    usePostApplicationDetailsMutation();

  const getValidationSchema = useValidationSchema();

  const dispatch = useDispatch();

  const initialValues = useMemo(
    () => ({
      id: applicationId ?? 0,
      utilityId: 1,
      applicationTypeId: applicationTypes["Net Metering"],
      utilityConsumerId:
        consumerData?.id ?? applicationData?.utilityConsumerId ?? "",
      consumerName: consumerData?.consumerName ?? "",
      tariffName: consumerData?.tariffName ?? "",
      consumerNumber:
        applicationData?.consumerNumber ?? consumerData?.consumerNumber ?? "",
      applicant: {
        id: applicationData?.applicant?.id ?? consumerData?.applicant?.id ?? 0,
        applicantTypeId: consumerData?.applicant?.applicantType?.id ?? "",
        applicantName: consumerData?.applicant?.applicantName ?? "",
        panNumber:
          applicationData?.applicant?.panNumber ??
          consumerData?.applicant?.panNumber ??
          "",
        gstNumber:
          applicationData?.applicant?.gstNumber ??
          consumerData?.applicant?.gstNumber ??
          "",
        occupancyTypeId: consumerData?.applicant?.occupancyType?.id ?? "",
        isGSTRegistered:
          applicationData?.applicant?.isGSTRegistered ??
          consumerData?.applicant?.isGSTRegistered ??
          "",
        addressType: consumerData?.applicant?.addressType ?? "",
        correspondenceAddress:
          consumerData?.applicant?.correspondenceAddress ?? "",
        city: consumerData?.applicant?.city ?? "",
        pinCode: consumerData?.applicant?.pinCode ?? "",
        email: consumerData?.applicant?.email ?? "",
        // isEmailVerified: !!applicationData?.applicant?.email ?? false,
        mobileNumber: consumerData?.applicant?.mobileNumber ?? "",
        landLine: consumerData?.applicant?.landLine ?? "",
        alternateEmail: consumerData?.applicant?.alternateEmail
          ? consumerData?.applicant?.alternateEmail?.split(", ")
          : [],
        alternateMobileNumber:
          consumerData?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },
      authorizedPerson: {
        isAuthorizedPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email ===
              applicationData.user.email &&
            applicationData.authorizedPerson.mobileNumber ===
              applicationData.user.mobileNumber
          : true,
        isEditablePerson:
          applicationData?.authorizedPerson?.isEditablePerson ?? false,
        id: applicationData?.authorizedPerson?.id ?? 0,
        salutation: applicationData?.authorizedPerson?.salutation ?? "",
        firstName: applicationData?.authorizedPerson?.firstName ?? "",
        middleName: applicationData?.authorizedPerson?.middleName ?? "",
        lastName: applicationData?.authorizedPerson?.lastName ?? "",
        designation: applicationData?.authorizedPerson?.designation ?? "",
        email: applicationData?.authorizedPerson?.email ?? "",
        mobileNumber: applicationData?.authorizedPerson?.mobileNumber ?? "",
        landLine: applicationData?.authorizedPerson?.landLine ?? "",
        image: "",
        imageUrl: applicationData?.authorizedPerson?.imageUrl ?? "",
        isUnRegisteredPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
      },
      utilityAddress: {
        id: applicationData?.utilityAddress?.id ?? 0,
        premiseTypeId: consumerData?.utilityAddress?.premiseType?.id ?? "",
        premiseUsagePurpose:
          consumerData?.utilityAddress?.premiseUsagePurpose ?? "",
        premisesAreaId: consumerData?.utilityAddress?.premiseArea?.id ?? "",
        zoneName: consumerData?.utilityAddress?.zone?.name ?? "",
        otherZone: "",
        blockName: consumerData?.utilityAddress?.block?.name ?? "",
        otherBlock: "",
        roadName: consumerData?.utilityAddress?.road?.name ?? "",
        otherRoad: "",
        buildingName: consumerData?.utilityAddress?.building?.name ?? "",
        otherBuilding: "",
        floors:
          consumerData?.utilityAddress?.floors?.map(({ id, name }) => ({
            label: name,
            value: id,
          })) ?? [],
        unitName: consumerData?.utilityAddress?.unitName ?? "",
        nearestLandmark: consumerData?.utilityAddress?.nearestLandmark ?? "",
        isBPL: consumerData?.utilityAddress?.isBPL ?? "",
        // isRural: consumerData?.utilityAddress?.isRural ?? "",
      },
      connectionLoad: {
        id: applicationData?.connectionLoad?.id ?? 0,
        powerRequirement:
          consumerData?.connectionLoad?.totalPowerRequirement ?? "",
        powerRequirementUnit:
          consumerData?.connectionLoad?.totalPowerRequirement <= 99 ? 1 : 2,
        isGreenPowerRequired: !!consumerData?.connectionLoad?.greenPower,
      },
      solar: {
        id: applicationData?.solar?.id ?? 0,
        developerName: applicationData?.solar?.developerName ?? "",
        gEDANumber: applicationData?.solar?.gedaNumber ?? "",
        cEIApprovalNumber: applicationData?.solar?.ceiApprovalNumber ?? "",
        capacity: applicationData?.solar?.capacity ?? "",
        capacityUnit: applicationData?.solar?.capacityUnit ?? "",
        proposedDate: applicationData?.solar?.proposedDate
          ? convertUTCDateToLocalDate(applicationData.solar.proposedDate)
          : "",
        type: applicationData?.solar?.type ?? "",
        endUse: applicationData?.solar?.endUse ?? "",
        isRegisteredREC: applicationData?.solar?.isRegisteredREC ?? false,
        energySettlement: applicationData?.solar?.energySettlement ?? "",
        existingSolarCapacity:
          applicationData?.solar?.existingSolarCapacity ?? "",
        otherPowerCapacity: applicationData?.solar?.otherPowerCapacity ?? "",
        openAccess: applicationData?.solar?.openAccess ?? "",
        proposedProjectLocation:
          applicationData?.solar?.proposedProjectLocation ?? "",
      },
      documents: applicationData?.documents?.filter(({ id }) => id) ?? [],
      declaration: {
        declarationId: applicationData?.declaration?.id ?? 0,
        isConsumerDuesOutstanding:
          !!applicationData?.declaration?.consumerDuesOutstanding ?? false,
        consumerDuesOutstanding:
          applicationData?.declaration?.consumerDuesOutstanding ?? "",
        isPremisesDuesOutstanding:
          !!applicationData?.declaration?.premisesDuesOutstanding ?? false,
        premisesDuesOutstanding:
          applicationData?.declaration?.premisesDuesOutstanding ?? "",
        isFirmDuesOutstanding:
          !!applicationData?.declaration?.firmDuesOutstanding ?? false,
        firmDuesOutstanding:
          applicationData?.declaration?.firmDuesOutstanding ?? "",
      },
      extraValues: {
        requiredDocumentsCount: 0,
        isPanNumber: !!consumerData?.applicant?.panNumber,
        userMobileNumber: applicationData?.user?.mobileNumber ?? "",
        userEmail: applicationData?.user?.email ?? "",
        isGstNumber: !!consumerData?.applicant?.gstNumber,
      },
      inProgressApplicationId: consumerData?.inProgressApplicationId ?? 0,
      isAllApplicationsProcessCompleted:
        consumerData?.isAllApplicationsProcessCompleted ?? false,
      payableBillAmount: consumerData?.payableBillAmount ?? 0,
      isIGSTExempted: consumerData?.isIGSTExempted ?? false,
    }),

    [applicationData, consumerData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: getValidationSchema(currentStep),

    onSubmit: async (values, actions) => {
      if (currentStep === steps.length - 1) {
        createNetMetering(values);
      } else if (
        values?.inProgressApplicationId !== applicationData?.id &&
        !values.isAllApplicationsProcessCompleted
      ) {
        toast.error(
          "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
        );
      } else if (values.payableBillAmount > 0) {
        toast.error(
          `You have an unpaid amount of ${values.payableBillAmount} RS. Please pay first to continue.`
        );
      } else {
        setCurrentStep((prev) => prev + 1);
        actions.setTouched({});
      }
    },
  });

  const { values, handleSubmit, initialValues: formikInitialValues } = formik;

  const {
    documents,
    extraValues: { requiredDocumentsCount },
  } = values;

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: "Application Details",
        isCompleted: currentStep >= 1,
        component: <ApplicantDetails formik={formik} />,
      },
      {
        id: 2,
        name: "Authorized Person Details",
        isCompleted: currentStep >= 2,
        component: <AuthorizedPersonDetails formik={formik} />,
      },
      {
        id: 3,
        name: "Utility Address",
        isCompleted: currentStep >= 3,
        component: <AddressForPower isDisable formik={formik} />,
      },
      {
        id: 4,
        name: "Project Details",
        isCompleted: currentStep >= 4,
        component: <SolarDetails formik={formik} />,
      },
      {
        id: 5,
        name: "Document to Submit",
        isCompleted: currentStep >= 5,
        component: <DocumentToSubmit formik={formik} />,
      },
      {
        id: 6,
        name: "Declaration",
        isCompleted: currentStep === 6,
        component: <Declaration formik={formik} />,
      },
    ],
    [currentStep, formik]
  );

  useEffect(() => {
    if (applicationId) {
      getApplicationData(applicationId);
      // setCompletedSteps(steps.length);
    }
  }, [applicationId]);

  const getCustomerDetails = async (utilityConsumerId) => {
    try {
      await getUtilityConsumerDetails(utilityConsumerId);
      if (
        applicationId &&
        values.utilityConsumerId !== formikInitialValues.utilityConsumerId
      ) {
        setApplicationData({});
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (values.utilityConsumerId) {
      getCustomerDetails(values.utilityConsumerId);
    }
  }, [values.utilityConsumerId]);

  const getApplicationData = async (applicationId) => {
    try {
      const res = await getApplicationDetails(applicationId);
      setApplicationData(res?.data);
    } catch (error) {}
  };

  useEffect(() => {
    dispatch(setValues(values));
  }, [values, dispatch]);

  const validateDocument = () => {
    const requiredUploadedDocumentsCount = documents.filter(
      ({ documentRequiredEnum }) => getIsDocumentRequired(documentRequiredEnum)
    ).length;

    if (
      currentStep === 4 &&
      requiredUploadedDocumentsCount !== requiredDocumentsCount
    ) {
      toast.error("Please upload all required documents");
    }
  };

  const createNetMetering = async (values) => {
    const imageKeyToRemove = values.authorizedPerson.image
      ? "authorizedPerson.imageUrl"
      : "authorizedPerson.image";
    const cityId = values.applicant.city?.id ?? "";
    const id = values.applicant.id;

    // converts document array to key-value pair
    const payloadWithUpdatedDocumentKeys = convertDocumentKeys(values);

    const applicationDetails = omit(payloadWithUpdatedDocumentKeys, [
      "applicant.city",
      "connectionLoad",
      "tariffName",
      "inProgressApplicationId",
      "isAllApplicationsProcessCompleted",
      "payableBillAmount",
      "consumerName",
      "utilityAddress",
      "connectionLoad",
      "consumerNumber",
      "authorizedPerson.isAuthorizedPerson",
      "authorizedPerson.isUnRegisteredPerson",
      "declaration.isConsumerDuesOutstanding",
      "declaration.isPremisesDuesOutstanding",
      "authorizedPerson.isEditablePerson",
      "declaration.isFirmDuesOutstanding",
      "documents",
      "extraValues",
      "isIGSTExempted",
      imageKeyToRemove,
    ]);

    const currentGstNumber = applicationId
      ? applicationData?.applicant?.gstNumber
      : consumerData?.applicant?.gstNumber;

    const currentPanNumber = applicationId
      ? applicationData?.applicant?.panNumber
      : consumerData?.applicant?.panNumber;

    const filteredValues = filterEmptyStrings({
      ...applicationDetails,
      applicant: {
        ...applicationDetails.applicant,
        alternateEmail: applicationDetails.applicant.alternateEmail.length
          ? applicationDetails.applicant.alternateEmail.join(", ")
          : "",
        cityId,
        id:
          applicationDetails.applicant.gstNumber !== (currentGstNumber ?? "") ||
          applicationDetails.applicant.panNumber !== (currentPanNumber ?? "")
            ? 0
            : id,
      },
    });

    // remove entries that contain only id field
    const payload = Object.entries(filteredValues).reduce(
      (res, [key, value]) => {
        if (
          Array.isArray(value) ||
          typeof value !== "object" ||
          value instanceof File ||
          Object.keys(value).length > 1
        ) {
          return { ...res, [key]: value };
        }
        return res;
      },
      {}
    );

    payload.solar.proposedDate = values.solar.proposedDate.format("YYYY-MM-DD");
    const formData = new FormData();
    generateFormData(formData, {
      ...payload,
      declaration: filteredValues.declaration,
    });

    try {
      await submitApplication(formData);
      if (!applicationId) {
        navigate(myRequestRoute);
      }
      handleClose();
    } catch (error) {}
  };

  return (
    <div className={!applicationId ? "h-100" : ""}>
      <Backdrop
        open={
          isApplicationDataLoading ||
          isConsumerDetailsLoading ||
          isApplicationDataFetching ||
          isConsumerDataFetching ||
          (!!applicationId && !consumerData)
        }
      />
      {!applicationId && (
        <BreadCrumb
          title={t("Grid Connectivity to proposed Solar Project")}
          pageTitle={t("Power Applications")}
        />
      )}
      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          activeStepIndex={currentStep}
          validateDocument={validateDocument}
          setActiveStepIndex={setCurrentStep}
          isLoading={isLoading}
          formik={formik}
          isDraftDisabled={true}
        />
      </div>
    </div>
  );
};

NetMetering.propTypes = {
  applicationId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(NetMetering);
